import { Box, Link, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { appVersion, FooterConstants } from '@/src/shared/constants/CommonConstants';
import {
    getReleaseNotesSeen,
    updateReleaseNotesSeen,
} from '@/src/shared/services/apiServices/usersApi';
import type { RootState } from '@/src/store';
import { userSlice } from '@/src/store/apps/user';
import { primaryOrange } from '@/src/styles/theme/lightThemeOptions';
import { getBlobUrl } from '@/src/utils/commonUtil';

import CancelDialog from '../../Modal/Cancel/CancelDialog';

export const ReleaseNotesDialog = () => {
    const { t } = useTranslation('modal');
    const user = useSelector((state: RootState) => state.user['state/user']);
    const releaseNotesLastCheckedVersion = useSelector(
        (state: RootState) => state.user['state/releaseNotesLastCheckedVersion']
    );
    const tourCompleted = useSelector((state: RootState) => state.user['state/tourCompleted']);
    const dispatch = useDispatch();
    const userEmail = user?.mail;

    const [open, setOpen] = useState(false);
    const releaseNotesUrl = getBlobUrl(FooterConstants.releaseNotes, 'pdf', 'gingerbot/org');

    useEffect(() => {
        if (!tourCompleted) return;
        if (!userEmail || releaseNotesLastCheckedVersion === appVersion) return;

        const checkReleaseNotes = async () => {
            try {
                const response = await getReleaseNotesSeen(appVersion || '');
                const isSeen = response.data?.isSeen === true;
                if (!isSeen) setOpen(true);

                dispatch(userSlice.actions.releaseNotesLastCheckedVersion(appVersion));
            } catch (error) {
                console.error('Error checking release notes', error);
            }
        };

        checkReleaseNotes();
    }, [userEmail, tourCompleted, releaseNotesLastCheckedVersion, dispatch]);

    const handleReleaseNotes = async () => {
        try {
            await updateReleaseNotesSeen(appVersion || '');
        } catch (error) {
            console.error('Error marking release notes as seen', error);
        }

        setOpen(false);
    };

    return (
        <CancelDialog
            restProps={{
                sx: {
                    '& .MuiPaper-root': {
                        width: '500px',
                    },
                    '& .buttonsWrapper': {
                        padding: '0 !important',
                    },
                },
            }}
            handleClose={handleReleaseNotes}
            open={open}
            title={t('modal.releasenotesmodal.title')}
            dialogContent={
                <Box display="flex" flexDirection="column" gap="8px">
                    <Typography sx={{ padding: '0 !important', whiteSpace: 'pre-line' }}>
                        {t('modal.releasenotesmodal.content')}
                    </Typography>
                    <Link
                        href={releaseNotesUrl}
                        target="_blank"
                        onClick={handleReleaseNotes}
                        style={{ textDecoration: 'none', color: primaryOrange, padding: 0 }}
                    >
                        {`${t('modal.releasenotesmodal.version')} ${appVersion}`}
                    </Link>
                </Box>
            }
        />
    );
};
